// Get firestore database
import { firestore } from "@/firebase/firestore";

/** A DAO with generic methods usable for multiple types of entities */
class GenericDao {
  /**
   * Flattens the document snapshot and returns it as a plain JS object.
   *
   * @param {FirebaseFirestore.DocumentSnapshot} snapshot Document snapshot.
   * @returns {*} Contents of the snapshot as a plain JS object.
   */
  dataFromDoc(snapshot) {
    return { ...snapshot.data(), id: snapshot.id };
  }

  /**
   * Deletes every document in a given collection in the database.
   *
   * @param {string} collection The name of the collection.
   * @returns {Promise<Array<FirebaseFirestore.WriteResult>} A Promise with all the values for each of the promises that are
   * produced in the deletion.
   */
  async deleteAll(collection) {
    let collectionRef = this.getCollectionRef(collection);
    let snapshot = await collectionRef.get();
    let promises = [];
    snapshot.docs.forEach(doc => {
      // console.log(`Deleting document '${collection}/${doc.id}'`);
      let deletionPromise = doc.ref.delete();
      promises.push(deletionPromise);
    });
    return promises;
  }

  /**
   * Deletes a document with the provided id from a collection.
   *
   * @param {string} collection The name of the collection.
   * @param {string} id The identifier for the document.
   * @returns {Promise<boolean>} A Promise with the result of the operation.
   */
  async deleteById(collection, id) {
    // console.log(`[genericDao.deleteById] /${collection}/${id}`);
    let documentRef = this.getDocumentRef({ collection: collection, id: id });
    await documentRef.delete();
    return true;
  }

  /**
   * @param {string} collection The name of the collection.
   * @returns {Promise} A Promise with all the objects of a collection.
   */
  async getAll(collection) {
    // console.log(
    //   `[genericDao.getAll] Retrieving all the documents in /${collection}`
    // );
    let collectionRef = this.getCollectionRef(collection);
    let snapshot = await collectionRef.get();

    let docSnaps = [];
    snapshot.forEach(doc => {
      docSnaps.push(doc);
    });

    return docSnaps;
  }

  /**
   * @param {*} collection Collection whose reference is to be retrieved.
   * @returns {FirebaseFirestore.CollectionReference} A CollectionReference from the database.
   */
  getCollectionRef(collection) {
    return firestore.collection(collection);
  }

  /**
   * Gets the data from an object to a javascript object from an
   * instance.
   *
   * @param {*} obj The object which data is to be retrieved.
   * @returns A javascript object holding the data.
   */
  getData(obj) {
    let data = {};
    Object.keys(obj).forEach(key => {
      let value = obj[key];
      if (value !== undefined && key !== "collection") {
        data[key] = value;
      }
    });
    return data;
  }

  /**
   * @param {*} entity Entity for which a reference is to be retrieved.
   * @returns A DocumentReference representing an entity in the database.
   */
  getDocumentRef(entity) {
    // TODO Handle errors for both function calls
    let collectionRef = this.getCollectionRef(entity.collection);
    let ref = entity.id ? collectionRef.doc(entity.id) : collectionRef.doc();
    return ref;
  }

  /**
   * Uses a document id to retrieve it from database within a Promise.
   *
   * @param {string} collection The name of the collection from which the
   * document is to be retrieved.
   * @param {string} id The identifier of the document to retrieve.
   * @returns {Promise} A promise with the document in form of a plain javascript object.
   */
  async getById(collection, id) {
    let documentRef = this.getDocumentRef({ id: id, collection: collection });
    // console.log(`Retrieving document: ${collection}${id}`);
    let docSnapshot = await documentRef.get();
    // TODO throw not found on docSnapshot.data() === undefined
    return this.dataFromDoc(docSnapshot);
  }

  /**
   * Saves the entity to the database merging data into the appropriate maps.
   *
   * @param {*} entity The entity to save in database.
   * @returns {Promise<Entity>} A Promise with the database values in an entity.
   */
  async save(entity) {
    return this.insert(entity, true);
  }

  /**
   * Saves the entity to the database without merging data into the appropriate
   * maps, but overwriting them.
   *
   * @param {*} entity The entity to save in database.
   * @returns {Promise} A Promise with the database values in an entity.
   */
  async forceSave(entity) {
    return this.insert(entity, false);
  }

  /**
   * Saves the entity to the database, to the collection defined in
   * entity.collection.
   *
   * @param {*} entity JS object representing the entity to save in database.
   * @returns {Promise} A Promise with the database values in an entity.
   */
  async insert(entityMap, merge) {
    // console.log(
    //   `[genericDao.save] Saving to /${entityMap.collection}: ${JSON.stringify(
    //     entityMap
    //   )}`
    // );
    let ref = this.getDocumentRef(entityMap);
    let data = this.getData(entityMap);
    await ref.set(data, { merge: merge });

    entityMap.id = ref.id;

    let docSnap = await ref.get();

    // Should be updated value
    return this.dataFromDoc(docSnap);
  }
}

const dao = new GenericDao();
export default dao;
